import React, { useState, useEffect } from 'react';
import { bool, string, object } from 'prop-types';
import { connect } from 'react-redux';
import { getContext } from '@fiverr-private/fiverr_context';
import {
    MachineTranslationButton,
    setStickyTranslationCookie,
    getStickyTranslationCookie,
    getStickyTranslationControlCookie,
    setStickyTranslationControlCookie
} from '@fiverr-private/machine_translation';
import { fetchUGC, toggleUGCView, ugcUpdated } from '../../actions/ugc';
import { MP_STICKY_EXPERIENCE } from '../../utils/mixPanelEvents';
import { BQ_STICKY_EXPERIENCE } from '../../utils/bigQueryEvents';
import { buildSourceInfo } from '../../utils/ugc/buildSourceInfo';
import isInStickyTranslationTest from '../../utils/experiments/stickyTranslation';
import SafePureComponentWrapper from '../shared/SafePureComponent';

const UGCTranslateButton = ({
    showOriginal,
    ugcMap,
    fetchUGC,
    toggleUGCView,
    ugcUpdated,
    isLeanStyle = false,
    componentName,
    useStickyTranslation = false
}, { general, biEvents }) => {
    const { gigId } = general;

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async() => {
            await stickyTranslate();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sourceInfo = buildSourceInfo({ componentName, gigId });

    const toggleUgc = async() => {
        const shouldFetchTranslation = showOriginal && !ugcMap.translated;

        setStickyTranslation(showOriginal);

        if (!shouldFetchTranslation) {
            toggleUGCView();
            return;
        }

        setLoading(true);
        await fetchUGC({ sourceInfo, shouldToggle: true });
        setLoading(false);
    };

    const onToggle = async() => {
        await toggleUgc();
        ugcUpdated();
    };

    const stickyTranslate = async() => {
        const startTime = performance.now();
        const { abTests } = getContext();

        if (isInStickyTranslationTest(abTests)) {
            if (isStickyTranslationEnabled()) {
                await toggleUgc();
                const translationTime = performance.now() - startTime;
                reportStickyTranslation(translationTime);
                setQualtricsStickyTranslationFlag();
            }
        } else if (isControlStickyTranslationEnabled()) {
            const translationTime = performance.now() - startTime;
            reportStickyTranslation(translationTime);
            setQualtricsStickyTranslationFlag();
        }
    };

    const reportStickyTranslation = (translationTime) => {
        biEvents.sendMixPanelEvent({ eventName: MP_STICKY_EXPERIENCE });
        biEvents.sendBigQueryEvent({
            eventName: BQ_STICKY_EXPERIENCE,
            params: {
                stickyTranslationResponseTime: translationTime
            }
        });
    };

    const setStickyTranslation = (sticky) => {
        const { abTests } = getContext();
        if (isInStickyTranslationTest(abTests)) {
            setStickyTranslationCookie(sticky);
        } else {
            setStickyTranslationControlCookie(sticky);
        }
    };

    const isStickyTranslationEnabled = () => {
        const stickyTranslationCookie = getStickyTranslationCookie();

        return useStickyTranslation && stickyTranslationCookie;
    };

    const isControlStickyTranslationEnabled = () => {
        const stickyTranslationCookie = getStickyTranslationCookie();
        const stickyTranslationControlCookie = getStickyTranslationControlCookie();

        return useStickyTranslation && (stickyTranslationCookie || stickyTranslationControlCookie);
    };

    /**
     * temp flag for qualtrics survey
     */
    const setQualtricsStickyTranslationFlag = () => window.fiverrStickyTranslation = true;

    const { LEAN_DEFAULT, DEFAULT } = MachineTranslationButton.STYLES;
    const style = isLeanStyle ? LEAN_DEFAULT : DEFAULT;

    return (
        <section className="ugc-translation-button-wrapper">
            <MachineTranslationButton loading={loading}
                toggled={!showOriginal}
                onClick={onToggle}
                style={style}
                sourceInfo={sourceInfo}/>
        </section>
    );
};

UGCTranslateButton.propTypes = {
    showOriginal: bool,
    isLeanStyle: bool,
    componentName: string
};

UGCTranslateButton.contextTypes = {
    general: object,
    biEvents: object
};

const mapStateToProps = ({ ugc }) => ({
    ...ugc
});

const ConnectedUGCTranslateButton = connect(mapStateToProps, {
    fetchUGC,
    toggleUGCView,
    ugcUpdated
})(UGCTranslateButton);

export { ConnectedUGCTranslateButton };

export default SafePureComponentWrapper(ConnectedUGCTranslateButton);
